import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

function formatDate(dateString) {
    if (!dateString) return "-";
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    } catch {
        return dateString;
    }
}

function formatCurrency(value, displayCurrency = "AED") {
    if (value == null || value === undefined) return "-";
    const numValue = Number(value);
    if (isNaN(numValue)) return "-";
    return `${displayCurrency} ${Math.round(numValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const ForecastSubtitle = React.memo(({ salesperson, region, fullScreen }) => (
    <p
        style={{
            color: "#94a3b8",
            fontSize: fullScreen ? "14px" : "13px",
            margin: "0 0 16px 0",
        }}
    >
        Salesperson: <strong style={{ color: "#e2e8f0" }}>{salesperson}</strong> · Region:{" "}
        <strong style={{ color: "#e2e8f0" }}>{region === "all" ? "All Regions" : region}</strong>
    </p>
));

const ForecastHeader = React.memo(({ year, onMaximize, actionButtons }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
            <h2 style={{ fontSize: "32px", margin: 0, color: "#fff", fontWeight: 800 }}>Forecasted Booking – {year}</h2>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
            {actionButtons}
        </div>
    </div>
));

const ForecastToggleBar = React.memo(({ hasMore, showAll, totalCount, onToggle, fullScreen }) => {
    if (!hasMore) return null;
    return (
        <div style={{
            marginBottom: fullScreen ? "16px" : "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: fullScreen ? "0" : "8px 0"
        }}>
            <span style={{ color: fullScreen ? "#94a3b8" : theme.colors.text.secondary, fontSize: fullScreen ? "14px" : "13px" }}>
                Showing {showAll ? totalCount : 10} of {totalCount} opportunities
            </span>
            <button
                onClick={onToggle}
                style={{
                    padding: fullScreen ? "8px 16px" : "6px 12px",
                    background: showAll ? (fullScreen ? "rgba(30, 41, 59, 0.6)" : theme.colors.background.card) : theme.colors.primary,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: fullScreen ? "8px" : "6px",
                    cursor: "pointer",
                    fontSize: fullScreen ? "13px" : "12px",
                    fontWeight: 600,
                    transition: "all 0.2s ease"
                }}
            >
                {showAll ? "Show Top 10" : "Show All"}
            </button>
        </div>
    );
});

const ForecastTable = React.memo(({ rows, onSort, getSortIcon, displayCurrency = "AED" }) => (
    <table
        style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0",
            fontSize: 13,
            color: "#e5e7eb",
        }}
    >
        <thead>
            <tr>
                <th
                    style={{
                        textAlign: "left",
                        padding: "16px",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        fontWeight: 600,
                        color: "#94a3b8",
                        whiteSpace: "nowrap",
                        background: "rgba(15, 23, 42, 0.5)",
                        cursor: "pointer"
                    }}
                    onClick={() => onSort("opportunity_code")}
                >
                    Opportunity No {getSortIcon("opportunity_code")}
                </th>
                <th
                    style={{
                        textAlign: "left",
                        padding: "16px",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        fontWeight: 600,
                        color: "#94a3b8",
                        background: "rgba(15, 23, 42, 0.5)",
                        cursor: "pointer"
                    }}
                    onClick={() => onSort("opportunity_name")}
                >
                    Opportunity Name {getSortIcon("opportunity_name")}
                </th>
                <th
                    style={{
                        textAlign: "left",
                        padding: "16px",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        fontWeight: 600,
                        color: "#94a3b8",
                        whiteSpace: "nowrap",
                        background: "rgba(15, 23, 42, 0.5)",
                        cursor: "pointer"
                    }}
                    onClick={() => onSort("value_aed")}
                >
                    Value ({displayCurrency}) {getSortIcon("value_aed")}
                </th>
                <th
                    style={{
                        textAlign: "left",
                        padding: "16px",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        fontWeight: 600,
                        color: "#94a3b8",
                        whiteSpace: "nowrap",
                        background: "rgba(15, 23, 42, 0.5)",
                        cursor: "pointer"
                    }}
                    onClick={() => onSort("expected_booking")}
                >
                    Expected Booking {getSortIcon("expected_booking")}
                </th>
                <th
                    style={{
                        textAlign: "left",
                        padding: "16px",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        fontWeight: 600,
                        color: "#94a3b8",
                        whiteSpace: "nowrap",
                        background: "rgba(15, 23, 42, 0.5)"
                    }}
                >
                    Stage
                </th>
            </tr>
        </thead>
        <tbody>
            {rows.map((row, idx) => (
                <tr key={`forecast-${row.opportunity_code}-${idx}`} style={{ transition: "background 0.2s" }} className="hover-row">
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontWeight: 500 }}>{row.opportunity_code || "-"}</td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.opportunity_name || ""}>{row.opportunity_name || "-"}</td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", whiteSpace: "nowrap", textAlign: "right", fontWeight: 600, color: theme.colors.primary }}>{formatCurrency(row.value_aed, displayCurrency)}</td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", whiteSpace: "nowrap" }}>{formatDate(row.expected_booking)}</td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", whiteSpace: "nowrap" }}>
                        {row.stage && String(row.stage).trim() ? row.stage : "-"}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
));

function ForecastedBooking({ filterContext, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "value_aed", direction: "desc" });
    const [showAll, setShowAll] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(`${API_BASE}/api/metrics/forecasted-booking`, {
                params: {
                    salesperson,
                    year,
                    region: region || "all",
                },
            });
            setRows(res.data?.data || []);
        } catch (err) {
            console.error("ForecastedBooking error", err);
            const errorMessage = err.response?.data?.detail || err.message || "Failed to load forecasted booking";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region]);

    useEffect(() => {
        if (!salesperson || !year) return;
        fetchData();
    }, [salesperson, year, fetchData]);

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (sortConfig.key === "expected_booking") {
                const aDate = aVal ? new Date(aVal) : new Date(0);
                const bDate = bVal ? new Date(bVal) : new Date(0);
                return sortConfig.direction === "asc"
                    ? aDate - bDate
                    : bDate - aDate;
            }

            if (sortConfig.key === "value_aed") {
                const aNum = Number(aVal || 0);
                const bNum = Number(bVal || 0);
                return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
            }

            const aStr = String(aVal || "").toLowerCase();
            const bStr = String(bVal || "").toLowerCase();
            if (sortConfig.direction === "asc") {
                return aStr.localeCompare(bStr);
            }
            return bStr.localeCompare(aStr);
        });
    }, [rows, sortConfig]);

    const displayRows = useMemo(() => (showAll ? sortedRows : sortedRows.slice(0, 10)), [showAll, sortedRows]);
    const hasMore = useMemo(() => sortedRows.length > 10, [sortedRows]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
        }));
    }, []);

    const getSortIcon = useCallback((key) => {
        if (sortConfig.key !== key) return " ↑↓";
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    }, [sortConfig.direction, sortConfig.key]);

    const hasData = useMemo(() => sortedRows.length > 0, [sortedRows]);

    const handleToggleShowAll = useCallback(() => {
        setShowAll(prev => !prev);
    }, []);

    const actionButtons = useMemo(() => (
        <>
            {onMaximize && !fullScreen && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        color: "#e5e7eb",
                        borderRadius: 8,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                    title="Maximize"
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
                </button>
            )}

            {fullScreen && onMaximize && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(244, 67, 54, 0.2)",
                        color: "#f44336",
                        border: "1px solid #f44336",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                >

                </button>
            )}
        </>
    ), [onMaximize, fullScreen]);

    if (!salesperson || !year) return null;

    if (fullScreen) {
        return (
            <div style={{ width: "100%", height: "100%", padding: "20px", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", position: "relative", display: "flex", flexDirection: "column" }}>
                <ForecastHeader year={year} onMaximize={onMaximize} actionButtons={actionButtons} />
                <ForecastSubtitle salesperson={salesperson} region={region} fullScreen={fullScreen} />

                {!loading && !error && hasData && (
                    <>
                        <ForecastToggleBar
                            hasMore={hasMore}
                            showAll={showAll}
                            totalCount={sortedRows.length}
                            onToggle={handleToggleShowAll}
                            fullScreen={true}
                        />
                        <div style={{
                            flex: 1,
                            overflow: "auto",
                            background: "rgba(30, 41, 59, 0.4)",
                            borderRadius: "16px",
                            border: "1px solid rgba(255,255,255,0.05)",
                            maxHeight: showAll ? "none" : "600px"
                        }}>
                                <ForecastTable
                                    rows={displayRows}
                                    onSort={handleSort}
                                    getSortIcon={getSortIcon}
                                    displayCurrency={displayCurrency}
                                />
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <DashboardCard
            title={`Forecasted Booking – ${year}`}
            actions={actionButtons}
            gradient="primary"
        >
            <ForecastSubtitle salesperson={salesperson} region={region} fullScreen={fullScreen} />

            {loading && <SkeletonCard height="300px" />}

            {error && (
                <EmptyState icon="️" message={error} size="small" />
            )}

            {!loading && !error && !hasData && (
                <EmptyState icon="" message="No forecasted booking opportunities found." size="medium" />
            )}

            {!loading && !error && hasData && (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <ForecastToggleBar
                        hasMore={hasMore}
                        showAll={showAll}
                        totalCount={sortedRows.length}
                        onToggle={handleToggleShowAll}
                        fullScreen={false}
                    />
                    <div style={{
                        flex: 1,
                        overflow: "auto",
                        margin: "-10px -24px 0 -24px",
                        padding: "0 24px",
                        maxHeight: showAll ? "none" : "400px"
                    }}>
                        <ForecastTable
                            rows={displayRows}
                            onSort={handleSort}
                            getSortIcon={getSortIcon}
                            displayCurrency={displayCurrency}
                        />
                    </div>
                </div>
            )}
        </DashboardCard>
    );
}

export default ForecastedBooking;
